import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ThemedEditIcon } from "assets/CXFIcons/Portal/Profile";
import SoftAvatar from "components/SoftAvatar";
import ProtectedButton from "authentication/ProtectedButton";
import { useRef, useState } from "react";
import SoftBox from "components/SoftBox";
import { toast } from "react-toastify";
import { CircularProgress, Typography } from "@mui/material";
import Compressor from "compressorjs";
import SoftTypography from "components/SoftTypography";
import { getEnv } from "helpers/environment";

export const EditPictureMenu = ({
  isReadOnlyAccess,
  pictureUpload,
  setPictureUpload,
  defaultPicture,
  value,
  params,
  isOwner,
  isDisabled
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const open = Boolean(anchorEl);
  const [showDefaultPicture, setShowDefaultPicture] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTriggerChangePicture = () => {
    inputRef.current?.click();
    handleClose();
  };

  const handleRemovePicture = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setPictureUpload("");
    setShowDefaultPicture(true);
    handleClose();
  };

  const handleShowPicture = () => {
    if (showDefaultPicture) return defaultPicture;

    if (pictureUpload) {
      return URL.createObjectURL(pictureUpload);
    }

    return value ? `${value}?v=${params}` : defaultPicture;
  };

  const compressImage = async (file) => {
    const maxPictureSize = parseInt(getEnv("MAX_ASSET_LOGO_SIZE"));

    const tryCompress = (quality, maxDimension) => {
      return new Promise((resolve, reject) => {
        new Compressor(file, {
          quality,
          maxWidth: maxDimension,
          maxHeight: maxDimension,
          mimeType: file.type,
          success: resolve,
          error: reject
        });
      });
    };

    const levels = [
      { quality: 0.8, size: 1280 },
      { quality: 0.7, size: 1280 },
      { quality: 0.6, size: 1280 },
      { quality: 0.5, size: 1280 },
      { quality: 0.4, size: 1280 },
      { quality: 0.3, size: 1280 },
      { quality: 0.6, size: 1024 },
      { quality: 0.5, size: 1024 },
      { quality: 0.4, size: 1024 },
      { quality: 0.3, size: 800 },
      { quality: 0.2, size: 800 }
    ];

    for (const level of levels) {
      const result = await tryCompress(level.quality, level.size);
      if (result.size <= maxPictureSize) {
        return result;
      }
    }

    return await tryCompress(0.2, 640);
  };

  const handleChangePicture = async (event) => {
    const file = event?.target?.files?.[0];

    if (!file) return;

    const maxPictureSize = parseInt(getEnv("MAX_ASSET_LOGO_SIZE"));

    try {
      if (file.size > maxPictureSize) {
        setIsCompressing(true);
        const compressedFile = await compressImage(file);
        setIsCompressing(false);

        setShowDefaultPicture(false);
        setPictureUpload(compressedFile);
      } else {
        setShowDefaultPicture(false);
        setPictureUpload(file);
      }
    } catch (error) {
      setIsCompressing(false);
      toast.error("Failed to process image. Please try a different image.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  return (
    <SoftBox sx={{ marginBottom: "4px" }}>
      <SoftBox sx={{ position: "relative", marginRight: "40px" }}>
        <SoftBox
          component="img"
          borderRadius="16px"
          sx={{
            objectFit: "fill",
            maxWidth: {
              sm: "120px",
              lg: "174px"
            },
            minHeight: {
              sm: "120px",
              lg: "164px"
            },
            maxHeight: "164px",
            width: "100vw",
            opacity: isCompressing ? 0.3 : 1,
            transition: "opacity 0.3s"
          }}
          src={handleShowPicture()}
        />
        {isCompressing && (
          <SoftBox
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="caption" sx={{ fontWeight: "medium" }}>
              Compressing...
            </Typography>
          </SoftBox>
        )}
        {!isDisabled && (
          <SoftBox
            sx={{
              inset: "auto 0 6px auto",
              position: "absolute"
            }}
          >
            <ProtectedButton isOwner={isOwner ?? true}>
              <SoftAvatar
                className="picture-upload"
                variant="rounded"
                shadow="sm"
                size="sm"
                bgColor="light"
                sx={{
                  marginLeft: "-18px",
                  marginTop: "-18px",
                  cursor: "pointer",
                  display: "flex",
                  borderRadius: "8px"
                }}
                onClick={handleOpenMenu}
              >
                <ThemedEditIcon />
              </SoftAvatar>
            </ProtectedButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button"
              }}
            >
              <MenuItem disabled={isReadOnlyAccess} onClick={handleTriggerChangePicture}>
                Change picture
              </MenuItem>
              {(value || pictureUpload) && !showDefaultPicture && (
                <MenuItem disabled={isReadOnlyAccess} onClick={handleRemovePicture}>
                  Remove
                </MenuItem>
              )}
            </Menu>
            <input
              style={{
                position: "fixed",
                left: "999px",
                opacity: 0
              }}
              ref={inputRef}
              onChange={(event) => {
                handleChangePicture(event);
                handleClose();
              }}
              type="file"
              accept="image/png, image/jpeg"
            />
          </SoftBox>
        )}
      </SoftBox>
      {!isDisabled && (
        <SoftTypography
          sx={{
            fontSize: "12px",
            fontWeight: "500"
          }}
          variant="body2"
          color="text"
        >
          ⓘ Images above 1mb will be compressed
        </SoftTypography>
      )}
    </SoftBox>
  );
};
